import { Box, Flex, Heading, Text } from '@chakra-ui/react'
import React from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

interface DailyMetricPoint {
  day: string
  value: number
}

interface DailyMetricBarChartProps {
  data: DailyMetricPoint[]
  title: string
  subtitle: string
  valueLabel: string
  headerAction?: React.ReactNode
}

const BAR_COLOR = '#3182CE'
const CURRENT_DAY_COLOR = '#90CDF4'

const formatDayLabel = (day: string): string => {
  const date = new Date(day)
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
}

const DailyMetricBarChart: React.FC<DailyMetricBarChartProps> = ({
  data,
  title,
  subtitle,
  valueLabel,
  headerAction,
}) => {
  // The series ends at the current day by construction; it is still in progress,
  // so it is styled distinctly to avoid reading as a usage drop.
  const lastIndex = data.length - 1
  const chartData = data.map(point => ({
    ...point,
    label: formatDayLabel(point.day),
  }))

  return (
    <Box bg='white' borderWidth='1px' borderColor='gray.200' borderRadius='lg' p={5}>
      <Flex justify='space-between' align='flex-start' gap={3} mb={4}>
        <Box>
          <Heading size='sm' mb={1}>
            {title}
          </Heading>
          <Text fontSize='sm' color='gray.500'>
            {subtitle}
          </Text>
        </Box>
        {headerAction}
      </Flex>
      <Box height='280px'>
        <ResponsiveContainer width='100%' height='100%'>
          <BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
            <CartesianGrid strokeDasharray='3 3' vertical={false} />
            <XAxis dataKey='label' fontSize={12} minTickGap={24} />
            <YAxis allowDecimals={false} fontSize={12} />
            <Tooltip
              formatter={value => [value, valueLabel]}
              labelFormatter={label => `${label}`}
            />
            <Bar dataKey='value' radius={[4, 4, 0, 0]}>
              {chartData.map((entry, index) => (
                <Cell
                  key={entry.day}
                  fill={index === lastIndex ? CURRENT_DAY_COLOR : BAR_COLOR}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  )
}

export default DailyMetricBarChart
